// Single source of truth for the Audit grid's column widths, order, and the
// sticky-identity offsets. Imported by BOTH AuditList (header) and AuditRow
// (body) so header and body column widths can NEVER diverge (the alignment
// acceptance criterion). Widths are in px.
//
// Why this file exists: index.css sets a GLOBAL `table { table-layout: fixed;
// width: 100% }`, which forces every column to divide the container width and
// causes the overlap/compression on narrow screens. The Audit <Table> overrides
// that with `tableLayout: 'auto'` + AUDIT_TABLE_MIN_WIDTH so columns take their
// declared widths and the TableContainer scrolls horizontally instead.

// Ordered column list (drives left-to-right order + the header/body match).
// `fixed: true` => width is pinned (width === minWidth === maxWidth) so the
// sticky left-offsets below stay exact; a growing frozen column would desync them.
export const AUDIT_COLUMNS = [
  { key: 'expander',        w: 40,  fixed: true,  sticky: true },
  { key: 'comCode',         w: 90,  fixed: true,  sticky: true, align: 'left' },
  { key: 'facility',        w: 170, fixed: true,  sticky: true, truncate: true, align: 'left' },
  { key: 'seasonId',        w: 80 },
  { key: 'seasonName',      w: 200, max: 260, truncate: true, align: 'left' },
  { key: 'startDate',       w: 110 },
  { key: 'endDate',         w: 110 },
  { key: 'priorSeason',     w: 150, align: 'left' },
  { key: 'priorYearSeason', w: 150, align: 'left' },
  { key: 'programType',     w: 100 },
  { key: 'season',          w: 105 },
  { key: 'year',            w: 80 },
  { key: 'lkOrSi',          w: 80 },
  { key: 'franchiseBucket', w: 150 },
  { key: 'bucketSeason',    w: 100 },
  { key: 'bucketStatus',    w: 130 },
  { key: 'rowSaved',        w: 90 },
  { key: 'status',          w: 120 },
  { key: 'action',          w: 115 },
];

const COL = AUDIT_COLUMNS.reduce((m, c) => { m[c.key] = c; return m; }, {});

// Cumulative left offsets for the sticky identity columns.
const stickyKeys = AUDIT_COLUMNS.filter((c) => c.sticky).map((c) => c.key);
export const STICKY_LEFT = (() => {
  const out = {};
  let acc = 0;
  for (const key of stickyKeys) { out[key] = acc; acc += COL[key].w; }
  return out;
})();
// x where the frozen region ends (first scrollable column starts here).
export const STICKY_END = stickyKeys.reduce((acc, k) => acc + COL[k].w, 0);

// Total min width -> forces horizontal scroll INTO the container, never the page.
export const AUDIT_TABLE_MIN_WIDTH = AUDIT_COLUMNS.reduce((sum, c) => sum + c.w, 0);

// z-index layers for the two-axis sticky (sticky header + sticky columns):
//   normal body cell        : 0 (static)
//   sticky-left body cell    : 1  (above scrolled body, below the header)
//   sticky (top) header cell : 2  (MUI stickyHeader default)
//   sticky corner (top+left) : 4  (above both)
const Z_BODY_STICKY = 1;
const Z_HEAD_STICKY = 4;

// Width + alignment sx for any column (fixed columns pin all three widths so the
// sticky offsets stay exact). Text columns declare `align: 'left'` in AUDIT_COLUMNS;
// everything else centers. `textAlign` here (an sx class) overrides the hard-coded
// `align="center"` prop on the cells, and beats the global `td { text-align:center }`
// in index.css — so header and body inherit alignment from this single source.
export function colSx(key) {
  const c = COL[key] || {};
  const textAlign = c.align || 'center';
  if (c.fixed) return { width: c.w, minWidth: c.w, maxWidth: c.w, textAlign };
  const sx = { minWidth: c.w, textAlign };
  if (c.max != null) sx.maxWidth = c.max;
  return sx;
}

// Single-line truncation for long free-text cells (Facility, Season Name).
// Pair with a `title` attribute so the full value is available on hover.
export const truncateSx = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// Changed-field (warehouse drift) cell cue. Applied to any cell whose warehouse
// value differs from the saved value (an Orig* marker is present). Deliberately
// louder than a thin underline so a changed field is scannable across a wide row:
// an OPAQUE light-red fill (safe under the sticky identity columns -> no bleed),
// bold text, and a solid red left-accent bar. Spread LAST in a cell's sx so it
// overrides the column's default background/weight.
export const DRIFT_CELL_SX = {
  backgroundColor: '#fdecea',            // opaque error-light fill
  fontWeight: 700,
  boxShadow: 'inset 4px 0 0 0 #d32f2f',  // error.main left-accent bar
};

// Sticky-left BODY cell: opaque paper bg so scrolled-under text can't bleed
// through (data rows are un-striped today -> one solid colour is enough).
export function stickyBodySx(key) {
  if (STICKY_LEFT[key] == null) return colSx(key);
  return {
    position: 'sticky',
    left: STICKY_LEFT[key],
    zIndex: Z_BODY_STICKY,
    backgroundColor: 'background.paper',
    ...colSx(key),
  };
}

// Sticky-left HEADER cell: matches MUI stickyHeader's opaque background.default
// and beats both the scrolling header cells and the sticky body cells.
export function stickyHeadSx(key) {
  if (STICKY_LEFT[key] == null) return colSx(key);
  return {
    position: 'sticky',
    left: STICKY_LEFT[key],
    top: 0,
    zIndex: Z_HEAD_STICKY,
    backgroundColor: 'background.default',
    ...colSx(key),
  };
}
